import React, { FC, useEffect, useState } from "react";
import { Checkbox, Form, Icon } from "semantic-ui-react";
import CustomInput from "@laborhack/input";
import Button from "@laborhack/custom-button";
import Joi from "@hapi/joi";
import { useApolloClient } from "@apollo/client";

import { FieldValue } from "types";
import {
  nonEmpty,
  phoneNumberCheck,
  phoneNumberDoesNotExist,
} from "helpers/utils/funtions";
import firebase from "helpers/firebase";
import { GET_USER_BY_EMAIL } from "graphql/query.constants";
import { Link, useHistory } from "react-router-dom";
import RequestFailed from "../../../../_components/RequestFailed";
import { PasswordGuide } from "./PasswordGuide";
import { logEvent } from "../../../../helpers/analytics";

export interface EmailPasswordSignInFormProps {}

export const EmailPasswordSignInForm: FC<EmailPasswordSignInFormProps> =
  ({}) => {
    const client = useApolloClient();
    const history = useHistory();

    useEffect(() => {
      logEvent("viewed_homeowner_registration_form");
    }, []);

    const defaultField = {
      value: "",
      isValid: false,
    };

    const emailDoesNotExist = async (email: string) => {
      try {
        await client.query({
          query: GET_USER_BY_EMAIL,
          variables: {
            email,
          },
        });

        return false;
      } catch (error) {
        return true;
      }
    };

    const [firstName, setFirstName] =
      useState<FieldValue<string>>(defaultField);
    const [lastName, setLastName] = useState<FieldValue<string>>(defaultField);
    const [phoneNumber, setPhoneNumber] =
      useState<FieldValue<string>>(defaultField);
    const [email, setEmail] = useState<FieldValue<string>>(defaultField);
    const [password, setPassword] = useState<FieldValue<string>>(defaultField);
    const [showPassword, setShowPassword] = useState(false);
    const [termsAccepted, setTermsAccepted] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [submissionError, setSubmissionError] = useState(false);

    const createAccountEnabled =
      firstName.isValid &&
      lastName.isValid &&
      email.isValid &&
      phoneNumber.isValid &&
      password.isValid &&
      termsAccepted;

    const createAccount = async () => {
      logEvent("sign_up_with_email_and_password_clicked");
      try {
        setSubmitting(true);
        const phoneNumberIsValid = await phoneNumberDoesNotExist(
          client,
          phoneNumber.value
        );
        const emailIsValid = await emailDoesNotExist(email.value);

        if (!phoneNumberIsValid || !emailIsValid)
          throw new Error("Phone number and/or email already exists");

        await firebase
          .auth()
          .createUserWithEmailAndPassword(email.value, password.value);

        const user = firebase.auth().currentUser;

        const actionCodeSettings = {
          url: window.location.origin,
          handleCodeInApp: true,
        };

        // add name to firebase profile
        await user?.updateProfile({
          displayName: `${firstName.value} ${lastName.value}`,
        });

        history.push(`/auth/finishSignIn?phoneNumber=${phoneNumber.value}`);
      } catch (error) {
        setSubmissionError(true);
      }

      setSubmitting(false);
    };

    if (submissionError) {
      return (
        <RequestFailed
          onRetry={() => {
            setSubmissionError(false);
          }}
        />
      );
    }

    return (
      <Form>
        <Form.Group widths="equal">
          <CustomInput
            autoComplete="given-name"
            required
            textLabel="First Name"
            value={firstName.value}
            onChange={(value: string, isValid: boolean) => {
              setFirstName({ value, isValid });
            }}
            validate={{
              check: nonEmpty,
              message: "Invalid Name",
            }}
            placeholder="First Name"
          />
          <CustomInput
            autoComplete="family-name"
            required
            textLabel="Last Name"
            value={lastName.value}
            onChange={(value: string, isValid: boolean) => {
              setLastName({ value, isValid });
            }}
            validate={{
              check: nonEmpty,
              message: "Invalid Name",
            }}
            placeholder="Last Name"
          />
        </Form.Group>
        <CustomInput
          autoComplete="tel"
          required
          textLabel="Phone Number"
          label="+234"
          type="tel"
          value={phoneNumber.value}
          onChange={(value: string, isValid: boolean) => {
            setPhoneNumber({ value, isValid });
          }}
          validate={{
            check: phoneNumberCheck,
            message: "Invalid Phone Number",
          }}
          asyncValidate={{
            check: (value: string) => phoneNumberDoesNotExist(client, value),
            message: "This phone number already exists",
          }}
          placeholder="8000000000"
        />
        <CustomInput
          autoComplete="email"
          type="email"
          required
          textLabel="Email Address"
          value={email.value}
          onChange={(value: string, isValid: boolean) => {
            setEmail({ value, isValid });
          }}
          validate={{
            check: (value: string) => {
              return !Joi.string()
                .email({
                  minDomainSegments: 2,
                  tlds: { allow: false },
                })
                .validate(value).error;
            },
            message: "Invalid Email",
          }}
          asyncValidate={{
            check: emailDoesNotExist,
            message: "This email already exists",
          }}
          placeholder="Enter Email Address"
        />
        <CustomInput
          autoComplete="new-password"
          required
          textLabel="Password"
          value={password.value}
          onChange={(value: string, isValid: boolean) => {
            setPassword({ value, isValid });
          }}
          validate={{
            check: (value: string) => {
              return !Joi.string()
                .pattern(
                  new RegExp(
                    /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/
                  )
                )
                .validate(value).error;
            },
            message: "Invalid Password",
          }}
          type={showPassword ? "text" : "password"}
          icon={
            <Icon
              link
              name={showPassword ? "eye slash" : "eye"}
              onClick={() => {
                setShowPassword((state) => !state);
              }}
            />
          }
          placeholder="Enter Password"
        />
        <PasswordGuide password={password.value} />
        <Checkbox
          style={{
            margin: "16px 0 24px 0",
          }}
          checked={termsAccepted}
          onChange={(e) => {
            setTermsAccepted((state) => !state);
          }}
          label={
            <label className="!text-sm !text-primary-500">
              Creating an account means you have agreed to our{" "}
              <Link to={"/terms"} className=" !text-accent-500">
                Terms and conditions
              </Link>{" "}
              and{" "}
              <Link to={"/privacy"} className=" !text-accent-500">
                Privacy policy
              </Link>
              .
            </label>
          }
        ></Checkbox>
        <Button
          size="big"
          type="circular"
          loading={submitting}
          disabled={!createAccountEnabled}
          onClick={createAccount}
        >
          Create Account
        </Button>
      </Form>
    );
  };
